import site from "../site";
import Helpers from "./helpers";
import Lockout from "./lockout";

declare var Mustache: any;

export default class Nav {

	private canHover = true;
	private navElement = document.querySelector('.js-nav') as HTMLElement;
	private openTrigger: HTMLButtonElement;
	private close: HTMLElement;
	private html: HTMLElement;
	private dimmers: NodeListOf<HTMLElement>;
	private secondaryNavs: NodeListOf<HTMLElement>;
	private topLevelLinks: NodeListOf<HTMLLinkElement>;
	private moreLinks: NodeListOf<HTMLButtonElement>;
	private closeTimer: ReturnType<typeof setTimeout>;
	private openTimer: ReturnType<typeof setTimeout>;
	private desktopNavActive = false;
	private htmlOpenClass = 'html--nav-open';
	private mobileNavHeight = 51; // items range between 49 - 51 in height, multiples of 51 are close enough to the real height that animations appear smooth and consistent
	private dimmerShowCSSClass = 'js-mega-nav-dimmer--show';
	private dimmerFadeCSSClass = 'js-mega-nav-dimmer--fade';
	private escapeEventListener: EventListener;
	private debouncedResetNav: (unLockPage: boolean) => void;

	constructor() {

		if (!this.navElement) return;

		this.openTrigger = document.querySelector('.js-nav-open-trigger');
		this.html = document.documentElement;
		this.close = document.querySelector('.js-nav-close');
		this.dimmers = document.querySelectorAll('.js-mega-nav-dimmer--main-nav');
		this.secondaryNavs = this.navElement.querySelectorAll('.js-nav-secondary');
		this.topLevelLinks = this.navElement.querySelectorAll('.js-nav-link-top-level');
		this.moreLinks = this.navElement.querySelectorAll('.js-nav-more');

		this.detectHover();
		this.drenchSpecificNav();

		this.initMobile();
		this.initDesktop();

		this.debouncedResetNav = Helpers.debounce((unLockPage: boolean) => this.resetNav(unLockPage), 200);
		window.matchMedia('only screen and (min-width: 980px)').onchange = () => { this.debouncedResetNav(true); };
		window.addEventListener('orientationchange', () => { this.debouncedResetNav(true); }, { passive: true });
		if (Helpers.activeSite() === "Toasty") {
			window.addEventListener('resize', () => { this.setSecondaryNavWidth(); }, { passive: true });
		}
		document.addEventListener('resetNav', () => {
			this.resetNav(false); 
		});
		this.escapeEventListener = (e: KeyboardEvent) => this.attachEscapeKeyboardEvent(e);

		if (this.isDesktopNav()) this.loadImages();

	}

	private initMobile() {

		this.calculateMobileNavHeights(true);
		this.openMobileNav();
		this.closeMobileNav();
		this.toggleMobileMoreLinks();

	}

	private drenchSpecificNav() {

		if (!document.body.classList.contains('theme-Drench')) {
			return;
		}

		const header = document.querySelector('.js-header') as HTMLElement;
		document.addEventListener('focusin', (e) => {
			const target = e.target as HTMLElement;
			if (header.contains(target)) {
				this.html.classList.add('header-has-focus');
			}
			if (target.matches('.js-header-search-input')) {
				this.resetNav(false);
			}
		});
		document.addEventListener('focusout', (e) => {
			this.html.classList.remove('header-has-focus');
		});

		document.querySelector('.js-header-search-input').addEventListener('click', (e) => {
			this.resetNav(false);
		});

	}

	private keyboardBlur(container) {
		const lastLink = this.getLastLinkInContainer(container);
		lastLink.addEventListener('blur', () => {
			const subNavTrigger = document.querySelector('[aria-controls="' + container.id + '"]');
			this.toggleSubNav(subNavTrigger);
		});
	}

	private getLastLinkInContainer(container) {
		const links = container.querySelectorAll('a');
		return links[links.length - 1];
	}

	private resetNav(unLockPage: boolean) {

		this.hideAllSubNavs();

		const tertiaryNavs = document.querySelectorAll('.js-nav-tertiary');

		for (let i = 0; i < tertiaryNavs.length; i++) {
			tertiaryNavs[i].classList.remove('is-active');
		}

		for (let j = 0; j < this.moreLinks.length; j++) {
			this.moreLinks[j].setAttribute('aria-expanded', 'false');
		}

		this.html.classList.remove(this.htmlOpenClass);

		this.hideDimmer();

		if (document.querySelector('.js-header-sticky')) {
			this.navElement.style.removeProperty('top');
		}

		this.calculateMobileNavHeights(false);

		document.removeEventListener("keydown", this.escapeEventListener);

		if (unLockPage) {

			Lockout.off();

		}

	}

	private calculateMobileNavHeights(setTertiaryHeights) {

		if (setTertiaryHeights) {

			const tertiaryNavs = document.querySelectorAll('.js-nav-tertiary') as NodeListOf<HTMLDivElement>;

			for (let i = 0; i < tertiaryNavs.length; i++) {

				const tertiaryNavItems = tertiaryNavs[i].querySelectorAll('.js-nav-tertiary-item');
				tertiaryNavs[i].style.maxHeight = tertiaryNavItems.length * this.mobileNavHeight + 'px';

			}

		}

		for (let j = 0; j < this.secondaryNavs.length; j++) {

			const secondaryNavItems = this.secondaryNavs[j].querySelectorAll('.js-nav-secondary-item');
			let maxHeight = secondaryNavItems.length * this.mobileNavHeight;
			const relevantTertiaryNavs = this.secondaryNavs[j].querySelectorAll('.js-nav-tertiary.is-active') as NodeListOf<HTMLElement>;

			if (relevantTertiaryNavs.length) {

				for (let k = 0; k < relevantTertiaryNavs.length; k++) { maxHeight += parseInt(relevantTertiaryNavs[k].style.maxHeight); }

			}

			this.secondaryNavs[j].style.maxHeight = maxHeight + 'px';

		}

	}

	private openMobileNav() {

		if (!this.openTrigger || !this.html) return;

		this.openTrigger.addEventListener('click', (e) => {
			if (document.querySelector('.js-header-sticky')) {
				this.navElement.style.top = document.documentElement.scrollTop + "px";
			} else {
				window.scrollTo(0, 0);
			}
			this.navElement.style.display = "block"; // Required as nav is hidden on page load to stop chrome from redrawing the page in it's active state after a refresh (?!)
			if (Helpers.isIOS()) {
				this.navElement.style.height = window.innerHeight + 'px'; // Required for iOS to take into account the browser nav height when 100dvh fails
			}
			this.html.classList.add(this.htmlOpenClass);

			Lockout.on();

			document.addEventListener("keydown", this.escapeEventListener);
		});

	}

	private closeMobileNav() {

		if (!this.close || !this.html) return;

		this.close.addEventListener('click', (e) => {
			this.html.classList.remove(this.htmlOpenClass);
			this.navElement.removeAttribute('style');
			Lockout.off();
		});

		document.removeEventListener("keydown", this.escapeEventListener);

	}

	private toggleMobileMoreLinks() {

		document.addEventListener('click', (e) => {
			const targetElement = e.target as HTMLElement;
			if (targetElement.matches('.js-nav-more')) {
				this.toggleSubNav(e.target);
			}
		});

	}

	private toggleSubNav(trigger) {

		const target = document.querySelector('#' + trigger.getAttribute('aria-controls'));

		if (!target) return;

		if (!target.classList.contains(this.templatedCSSClass())) {
			const subNavData = this.getSubNavData(target);
			if (subNavData) {
				this.templateSubNav(subNavData, target);
				this.keyboardBlur(target);
				this.calculateMobileNavHeights(true);
			}
		}

		if (this.isDesktopNav()) {

			this.hideAllSubNavs(trigger);
			if (this.closeTimer) clearTimeout(this.closeTimer);

		}

		const expanded = trigger.getAttribute('aria-expanded') === 'true' ? true : false;

		if (expanded) {

			target.classList.remove('is-active');
			document.removeEventListener("keydown", this.escapeEventListener);

			if (this.isDesktopNav()) { this.hideDimmer(); }

		} else {

			target.classList.add('is-active');
			this.showDimmer();
			document.addEventListener("keydown", this.escapeEventListener);

		}

		this.calculateMobileNavHeights(false);

		trigger.setAttribute('aria-expanded', !expanded);

	}

	private templateSubNav(subNavData, target) {
		const subNavTemplate = document.querySelector('#subNavTemplate').innerHTML;
		const navigationItems = this.isDesktopNav() ? subNavData.Items : subNavData.MobileItems;

		for (let i = 0; i < navigationItems.length; i++) {
			const secondaryNav = navigationItems[i];
			secondaryNav.NavId = Helpers.guid();
			for (let j = 0; j < secondaryNav.Items.length; j++) {
				const teriataryNav = secondaryNav.Items[j];
				teriataryNav.NavId = Helpers.guid();
			}
		}

		subNavData.Items = navigationItems;

		const subnavTemplated = Mustache.render(subNavTemplate, subNavData);

		const existingSubNav = target.querySelector('.c-nav__meganav');
		if (existingSubNav) {
			Helpers.removeElement(existingSubNav);
		}
		target.classList.remove('is-templated-desktop');
		target.classList.remove('is-templated-mobile');
		target.insertAdjacentHTML("afterbegin", subnavTemplated);
		target.classList.add(this.templatedCSSClass());
	}

	private templatedCSSClass() {
		return `is-templated-${this.isDesktopNav() ? 'desktop': 'mobile'}`;
	}

	private getSubNavData(target) {

		const subNavDataElement = target.querySelector('.js-sub-nav-data');

		if (!subNavDataElement) {
			return false;
		}

		return JSON.parse(subNavDataElement.innerHTML);
	}

	private loadImages() {

		const images = this.navElement.querySelectorAll('img');

		if (!images.length) return;

		for (let i = 0; i < images.length; i++) { images[i].classList.add('lazypreload'); }

	}

	private initDesktop() {

		if(Helpers.activeSite() === "Toasty") {
			this.setSecondaryNavWidth();
		}
		this.desktopSubNavOpen();
		this.attachDimmerEvents();

	}

	private setSecondaryNavWidth() {

		if (!this.isDesktopNav()) {
			for (let i = 0; i < this.secondaryNavs.length; i++) {
				this.secondaryNavs[i].style.width = 'auto';
			}
			return;
		}

		const bodyWidth = document.body.offsetWidth;

		for (let i = 0; i < this.secondaryNavs.length; i++) {
			this.secondaryNavs[i].style.width = bodyWidth + 'px';
		}

	}

	private desktopSubNavOpen() {

		if (!this.topLevelLinks) return;

		for (let i = 0; i < this.topLevelLinks.length; i++) {

			const currentNavLink = this.topLevelLinks[i];

			currentNavLink.addEventListener('mouseenter', (e) => {

				if (this.isDesktopNav() && this.canHover) {

					if (this.desktopNavActive) {
						this.toggleSubNav(e.target);
					} else {
						this.openTimer = setTimeout(() => {
							this.toggleSubNav(e.target);
						}, 350);
					}

				}

			});

			currentNavLink.addEventListener('mouseleave', (e) => {
				if (this.openTimer) {
					clearTimeout(this.openTimer);
				}
			});

			currentNavLink.addEventListener('click', (e) => {

				if (this.isDesktopNav() && this.hasSubnav(e.currentTarget)) {

					this.toggleSubNav(e.currentTarget);

					if (!this.canHover || Helpers.isUsingKeyboard()) { e.preventDefault(); } // if user is operating top level links in desktop mode on a touch device

				}

			});

		}
	}

	private hasSubnav(currentNavLink) {
		const subnavigationId = currentNavLink.getAttribute('aria-controls');
		return document.querySelector('#' + subnavigationId);
	}

	private hideAllSubNavs(excludeActiveElement?: HTMLElement) {

		for (let i = 0; i < this.secondaryNavs.length; i++) {
			this.secondaryNavs[i].classList.remove('is-active');
		}

		for (let j = 0; j < this.topLevelLinks.length; j++) {
			if (excludeActiveElement && this.topLevelLinks[j].isEqualNode(excludeActiveElement)) {
				continue;
			}
			this.topLevelLinks[j].setAttribute('aria-expanded', 'false');
		}

	}

	private attachDimmerEvents() {

		if (!this.dimmers.length) return;

		for (let i = 0; i < this.dimmers.length; i++) {
			this.dimmers[i].addEventListener('mouseover', (e) => {
				this.closeTimer = setTimeout(() => {
					if (!Helpers.isUsingKeyboard()) {
						this.hideDimmer();
						this.hideAllSubNavs();
					}
				}, 350);
			});
		}

		for (let i = 0; i < this.dimmers.length; i++) {
			this.dimmers[i].addEventListener('click', () => {
				this.hideDimmer();
				this.hideAllSubNavs();
			});
		}

		// if you hover out then back in clear the close timer

		this.navElement.addEventListener('mouseover', (e) => {
			const targetElement = e.target as HTMLElement;
			if (targetElement.matches('.c-nav__inner') || targetElement.closest('.c-nav__inner')) {
				clearTimeout(this.closeTimer);
			}
		});

	}

	private showDimmer() {

		if (!this.isDesktopNav()) return;

		Helpers.scopedLoop(this.dimmers, (dimmer) => {
			dimmer.classList.add(this.dimmerShowCSSClass);
			setTimeout(() => { dimmer.classList.add(this.dimmerFadeCSSClass); }, 10);
		}, this);

		this.html.classList.add(this.htmlOpenClass);

		this.desktopNavActive = true;

		const showDimmerEvent = Helpers.createCustomEventForDispatch('navDimmerActive');
		document.dispatchEvent(showDimmerEvent);

	}

	private hideDimmer() {

		Helpers.scopedLoop(this.dimmers, (dimmer) => {
			dimmer.classList.remove(this.dimmerFadeCSSClass);
			setTimeout(() => { dimmer.classList.remove(this.dimmerShowCSSClass); }, 10);
		}, this);

		this.html.classList.remove(this.htmlOpenClass);

		this.desktopNavActive = false;

		const hideDimmerEvent = Helpers.createCustomEventForDispatch('navDimmerClose');
		document.dispatchEvent(hideDimmerEvent);

	}

	private isDesktopNav() {

		return matchMedia('(min-width: 980px)').matches;

	}

	private attachEscapeKeyboardEvent(e: KeyboardEvent) {
		if (this.html.classList.contains(this.htmlOpenClass)) {
			const key = Helpers.getKeypressed(e);
			const isEscapeKey = key === 'Escape' || key === 27;
			if (isEscapeKey) {
				this.resetNav(true);
			}
		}
	}

	private detectHover() {
		window.addEventListener('touchstart', () => {
			this.canHover = false;
		}, false);
		const debounceMouseOverCheck = site.debounce(() => {
			this.canHover = true;
		}, 1000, null);
		this.navElement.addEventListener('mouseover', debounceMouseOverCheck, false);
	}

}
